import { computed, onMounted, watch } from 'vue'
import { useUserStore } from '~/stores/useUserStore'
import { useI18n } from 'vue-i18n'
import { useLogger } from '~/composables/useLogger'
import { useUserPermissions } from '~/composables/useUserPermissions'
import { useIdentity } from '~/composables/useIdentity'
import { NavMenuSection } from '~/enums'
import { resolveNavSection } from '~/utils/resolveNavSection'

export const useNavAuthMenu = () => {
  const logger = useLogger('useNavAuthMenu')
  const { isAuthenticated, logout, identity } = useIdentity()
  const userStore = useUserStore()
  const { t } = useI18n()
  const { roleNames } = useUserPermissions()

  const loadUserAccess = async (): Promise<void> => {
    if (!isAuthenticated.value) {
      return
    }

    await userStore.fetchUserAccess()
  }

  onMounted(loadUserAccess)
  watch(isAuthenticated, async (authenticated) => {
    if (authenticated) {
      await loadUserAccess()
    }
  })

  const primaryMenus = computed(() =>
    resolveNavSection(NavMenuSection.MAIN, userStore.access.permissions, roleNames.value, t)
  )

  const secondaryMenus = computed(() =>
    resolveNavSection(NavMenuSection.SECONDARY, userStore.access.permissions, roleNames.value, t)
  )

  const userMenus = computed(() =>
    resolveNavSection(NavMenuSection.USER, userStore.access.permissions, roleNames.value, t)
  )

  const userAvatarSrc = computed(() => {
    const userRecord = identity.value ?? userStore.user
    const name = userRecord === null || userRecord.name.length === 0 ? 'User' : userRecord.name
    if (userRecord !== null && userRecord.avatar.length > 0) return userRecord.avatar
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=5754e8&color=ffffff`
  })

  const handleLogout = async (beforeLogout: (() => void | Promise<void>) | null = null) => {
    try {
      if (beforeLogout && typeof beforeLogout === 'function') {
        await beforeLogout()
      }
      await logout()
      userStore.clearUserState()
      await navigateTo('/')
    } catch (error) {
      logger.error('handleLogout', error)
    }
  }

  return {
    primaryMenus,
    secondaryMenus,
    userMenus,
    userAvatarSrc,
    handleLogout
  }
}
